import { useMemo } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db } from '../datos/db.js'
import { citasDelCliente, type TramoConCita } from '../datos/consultas.js'
import { fechaLarga, hora } from '../datos/formato.js'
import { Boton } from '../comunes/Boton.js'
import { EstadoPill } from './Ficha.js'

/**
 * La solapa «Citas» del paciente (T23).
 *
 * Cuándo vino y cuándo vuelve, en dos grupos: **Próximas** y **Anteriores**.
 * Es la misma información que hasta T22 vivía como historial en la ficha, pero
 * ahora con las citas futuras que ahí no se veían en ningún lado del paciente.
 *
 * **Sólo Dexie:** lee la ventana operativa que este dispositivo tiene bajada.
 * Por eso el estado vacío dice exactamente eso, y no «sin citas»: una cita que
 * quedó fuera de la ventana no es una cita que no exista.
 *
 * El profesional de la fila es el recurso persona del tramo, resuelto por el
 * tipo de recurso igual que hace `elDia` con la columna del día.
 */
export function Citas({
  clienteId,
  onAbrirCita,
  onAgendar,
}: {
  clienteId: string
  onAbrirCita?: ((citaId: string) => void) | undefined
  onAgendar?: ((clienteId: string) => void) | undefined
}) {
  const citas = useLiveQuery(() => citasDelCliente(clienteId), [clienteId], [])
  const recursos = useLiveQuery(() => db.recurso.toArray(), [], [])
  const tipos = useLiveQuery(() => db.recurso_tipo.toArray(), [], [])

  // Quién es persona y quién es espacio lo dice el tipo, no el recurso: la fila
  // muestra «Dra. Benítez», no la lista cruda de lo que ocupa el tramo.
  const profesionalPorTramo = useMemo(() => {
    const porRecurso = new Map(recursos.map((r) => [r.id, r]))
    const esPersona = new Map(tipos.map((t) => [t.id, t.es_persona]))
    const mapa = new Map<string, string | null>()
    for (const c of citas) {
      const nombre =
        c.recursos
          .map((id) => porRecurso.get(id))
          .find((r) => r !== undefined && esPersona.get(r.recurso_tipo_id) === true)?.nombre ??
        null
      mapa.set(c.tramo.id, nombre)
    }
    return mapa
  }, [citas, recursos, tipos])

  // El corte lo hace el instante actual: la consulta ya devolvió el orden
  // resuelto (próximas de la más cercana hacia adelante, anteriores de la más
  // reciente hacia atrás), y acá sólo se parte.
  const ahora = new Date().toISOString()
  const proximas = citas.filter((c) => c.tramo.inicio > ahora)
  const anteriores = citas.filter((c) => c.tramo.inicio <= ahora)

  return (
    <div className="max-w-2xl">
      <div className="mb-5 flex flex-wrap justify-end gap-2">
        {onAgendar && (
          <Boton variante="primary" onClick={() => onAgendar(clienteId)}>
            Agendar cita
          </Boton>
        )}
      </div>

      {citas.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white py-12 text-center">
          <p className="txt-body">Sin citas en la ventana que este dispositivo tiene bajada.</p>
          {onAgendar && <p className="txt-meta mt-1">Las próximas aparecen al agendarlas.</p>}
        </div>
      ) : (
        <div className="space-y-6">
          <Grupo
            titulo="Próximas"
            citas={proximas}
            profesionalPorTramo={profesionalPorTramo}
            onAbrirCita={onAbrirCita}
          />
          <Grupo
            titulo="Anteriores"
            citas={anteriores}
            profesionalPorTramo={profesionalPorTramo}
            onAbrirCita={onAbrirCita}
          />
        </div>
      )}
    </div>
  )
}

function Grupo({
  titulo,
  citas,
  profesionalPorTramo,
  onAbrirCita,
}: {
  titulo: string
  citas: TramoConCita[]
  profesionalPorTramo: Map<string, string | null>
  onAbrirCita?: ((citaId: string) => void) | undefined
}) {
  if (citas.length === 0) return null

  return (
    <section>
      <h2 className="mb-2 text-[13px] font-medium text-slate-600">
        {titulo} ({citas.length})
      </h2>
      <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white">
        {citas.map((c) => (
          <FilaCita
            key={c.tramo.id}
            cita={c}
            profesional={profesionalPorTramo.get(c.tramo.id) ?? null}
            onAbrirCita={onAbrirCita}
          />
        ))}
      </ul>
    </section>
  )
}

function FilaCita({
  cita,
  profesional,
  onAbrirCita,
}: {
  cita: TramoConCita
  profesional: string | null
  onAbrirCita?: ((citaId: string) => void) | undefined
}) {
  const contenido = (
    <>
      <span className="min-w-0 flex-1">
        <span className="block text-[14px] font-medium text-slate-900">
          {fechaLarga(cita.tramo.inicio)}
        </span>
        <span className="block text-[13px] text-slate-500">
          <span className="tabular-nums">{hora(cita.tramo.inicio)}</span>
          {' · '}
          {profesional ?? 'sin profesional'}
        </span>
      </span>
      <span className="flex shrink-0 flex-col items-end gap-1">
        <EstadoPill estado={cita.cita.estado} />
        {cita.cita.origen === 'publico' && (
          <span className="text-xs text-slate-400">pidió por la web</span>
        )}
      </span>
    </>
  )

  if (!onAbrirCita) {
    return <li className="flex items-center gap-3 px-4 py-3">{contenido}</li>
  }

  return (
    <li>
      <button
        type="button"
        onClick={() => onAbrirCita(cita.cita.id)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-slate-50"
      >
        {contenido}
      </button>
    </li>
  )
}